import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { toast, Toaster } from "sonner";
import { Shield, Eye, Pencil, Trash2, UserPlus, LogIn, LogOut, Filter, ChevronDown, RefreshCw } from "lucide-react";
import AdminSubnav from "@/components/app/AdminSubnav";

const ACTION_LABELS = {
  role_update: { label: "Changement de rôle", color: "bg-blue-50 text-blue-700 border-blue-200", icon: Shield },
  profile_update: { label: "Modification profil", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Pencil },
  user_delete: { label: "Suppression utilisateur", color: "bg-red-50 text-red-700 border-red-200", icon: Trash2 },
  user_create: { label: "Création utilisateur", color: "bg-green-50 text-green-700 border-green-200", icon: UserPlus },
  impersonate_start: { label: "Entrée mode admin", color: "bg-purple-50 text-purple-700 border-purple-200", icon: LogIn },
  impersonate_stop: { label: "Sortie mode admin", color: "bg-gray-50 text-gray-600 border-gray-200", icon: LogOut },
};

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (action) params.action = action;
      const { data } = await api.get("/admin/audit-logs", { params });
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action]);

  const byAction = useMemo(() => {
    const map = {};
    logs.forEach((l) => { map[l.action] = (map[l.action] || 0) + 1; });
    return map;
  }, [logs]);

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <AdminSubnav />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Administration · Journal d'audit</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none">
            Historique des actions.
          </h1>
          <p className="text-gray-500 mt-3 max-w-lg">Qui a consulté, modifié ou supprimé quoi. Toutes les actions admin sont enregistrées.</p>
        </div>
        <button className="btn-secondary" onClick={load} data-testid="refresh-audit"><RefreshCw size={14} /> Rafraîchir</button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="ra-card p-4"><div className="overline mb-1">Total entrées</div><div className="num font-heading text-3xl font-black">{total}</div></div>
        <div className="ra-card p-4"><div className="overline mb-1">Profils modifiés</div><div className="num font-heading text-3xl font-black">{byAction.profile_update || 0}</div></div>
        <div className="ra-card p-4"><div className="overline mb-1">Rôles changés</div><div className="num font-heading text-3xl font-black">{byAction.role_update || 0}</div></div>
        <div className="ra-card p-4"><div className="overline mb-1">Entrées mode admin</div><div className="num font-heading text-3xl font-black">{byAction.impersonate_start || 0}</div></div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-6">
        <label className="overline flex items-center gap-1"><Filter size={12} /> Filtrer</label>
        <div className="relative">
          <select value={action} onChange={(e) => setAction(e.target.value)} className="ra-input pr-8 appearance-none" data-testid="filter-action">
            <option value="">Toutes les actions</option>
            <option value="impersonate_start">Entrée mode admin</option>
            <option value="impersonate_stop">Sortie mode admin</option>
            <option value="profile_update">Modification profil</option>
            <option value="role_update">Changement rôle</option>
            <option value="user_delete">Suppression utilisateur</option>
          </select>
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      <div className="ra-card" data-testid="audit-logs-card">
        {loading && <div className="progress-line" />}
        {(!loading && logs.length === 0) ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 flex items-center justify-center">
              <Shield className="text-gray-400" size={20} />
            </div>
            <p className="font-heading text-xl font-bold mb-1">Aucune entrée</p>
            <p className="text-sm text-gray-500">Les actions admin apparaîtront ici.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ra-table">
              <thead>
                <tr>
                  <th style={{width: 160}}>Date</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Cible</th>
                  <th>Détails</th>
                </tr>
              </thead>
              <tbody data-testid="audit-tbody">
                {logs.map((l) => {
                  const meta = ACTION_LABELS[l.action] || { label: l.action, color: "bg-gray-50 text-gray-700 border-gray-200", icon: Shield };
                  const Icon = meta.icon;
                  return (
                    <tr key={l.log_id} data-testid={`audit-row-${l.log_id}`}>
                      <td className="num text-xs text-gray-600">{l.timestamp ? new Date(l.timestamp).toLocaleString("fr-CA") : "—"}</td>
                      <td className="text-sm">{l.admin_email}</td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold border ${meta.color}`}>
                          <Icon size={12} /> {meta.label}
                        </span>
                      </td>
                      <td className="text-sm">
                        {l.details?.email || l.target_user_id || "—"}
                      </td>
                      <td className="text-xs text-gray-600">
                        {l.action === "role_update" && l.details && (
                          <span><span className="num font-mono">{l.details.from}</span> → <span className="num font-mono font-bold">{l.details.to}</span></span>
                        )}
                        {l.action === "profile_update" && l.details?.changes && (
                          <div className="space-y-0.5">
                            {Object.entries(l.details.changes).map(([k, v]) => (
                              <div key={k}><span className="font-mono text-gray-400">{k}:</span> {String(v)}</div>
                            ))}
                          </div>
                        )}
                        {l.action === "user_delete" && l.details && (
                          <span>{l.details.invoice_count} facture(s) supprimée(s)</span>
                        )}
                        {l.action === "impersonate_start" && l.details?.name && (
                          <span>Consultation de <span className="font-medium">{l.details.name}</span></span>
                        )}
                        {l.action === "impersonate_stop" && <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
